import React, { useMemo } from 'react';
import {
  Box, Field, FieldTypes, Inscription,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { REGEXPS, VALIDATORS } from '@/common';
import { INTEGER_VALUE_TEXT, INVALID_RANGE_TEXT, MUST_BE_GREATER_TEXT, REQUIRED_TEXT } from '@/common/constants';
import { usePlanFieldsState, useTemplateFieldState } from '@/modules/plan/PlanSetup/hooks';
import { PLAN_SETUP_FIELD } from '@/modules/plan/PlanSetup/PlanSetup.constants';
import { usePreviewStore } from '@/modules/plan/PlanSetup/stores';
import theme from '@/styles/theme';
import useFieldsWithDefaultValues from '@/utils/hooks/useFieldsWithDefaultValues';

import { RunOutOption } from '../../../Coverage/GracePeriod/hooks/useGracePeriodFields';
import { useGracePeriodStore } from '../../../Coverage/GracePeriod/stores';
import FinancialHeader from '../FinancialHeader';
import { useAccountFundingParamsStore } from '../stores';

import useBanksList from './useBanksList';
import useEOYPlanReconciliationFields from './useEOYPlanReconciliationFields';

const DAILY_KEY = 'DAILY';
const WEEKLY_KEY = 'WEEKLY';
const MONTHLY_KEY = 'MONTHLY';
const CUSTOM_DAY_FIELD_HINT = 'If a month has fewer days than the entered number, replenishment will be processed on the last day of this month';
const CUSTOM_DAY_ERROR_TEXT = 'This number does not match the range of available numbers (1-31)';
const PRE_FUND_SUBTITLE = 'Elevate holds a portion of member enrollments to support quick claim payments. Each month elections will be re-calculated and determine if additional funds will need to be collected';
const PRE_FUND_SUBFIELDS = {
  PERCENT: 'The percentage of prefund depends on replenishment frequency',
  ELECTION_CHANGE_PERCENTAGE: 'Increased elections from the previous pre-fund where additional funds will be collected',
};

const APPLY_FEE_TO_OPTIONS = [
  { label: 'All Active Accounts (Canceled and Active Policies)', value: 'ACTIVE_ACCOUNTS_ALL_PLANS' },
  { label: 'Canceled Plans with Active Accounts', value: 'ACTIVE_ACCOUNTS_CANCELED_PLANS' },
];

const FUNDING_REPLENISHMENT_METHOD_OPTIONS = [
  { label: 'Pull funds', value: 'PULL_FUNDS' },
  { label: 'Push funds', value: 'PUSH_FUNDS' },
];

const currencyValueValidate = yup
  .string()
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return value >= 1 && value <= 999999;
      }
      return true;
    },
    message: INVALID_RANGE_TEXT(1, 999999),
  })
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return Number.isInteger(value);
      }
      return true;
    },
    message: INTEGER_VALUE_TEXT,
  });

const percentValueValidate = yup
  .string()
  .test({
    test: (val) => {
      if (val) {
        const value = Number(val);
        return value >= 1 && value <= 100;
      }
      return true;
    },
    message: INVALID_RANGE_TEXT(1, 100),
  });

const FIELD_WRAPPER_STYLES = {
  borderTop: `1px solid ${theme.colors.border1}`,
  paddingTop: theme.spacings.m,
};
const ALTERNATIVE_FIELD_WRAPPER_STYLES = {
  borderBottom: `1px solid ${theme.colors.border1}`,
  paddingBottom: theme.spacings.m,
};

const useAccountFundingParamsFields = (isActivationMode?: boolean, isTemplateMode?: boolean, isEditMode?: boolean) => {
  const { runOut } = useGracePeriodStore((state) => state.state);
  const banksList = useBanksList();
  const sourceState = useAccountFundingParamsStore((state) => state.sourceState);
  const currentState = useAccountFundingParamsStore((state) => state.state);
  const isForfeitAllowed = useMemo(() => currentState?.isForfeitAllowed, [currentState]);

  const isParticipantPaidMaintenanceFee = useMemo(() => currentState?.isParticipantPaidMaintenanceFee, [currentState]);
  const errorFields = useAccountFundingParamsStore((state) => state.errorFields);
  const isPlanLevelFunding = useAccountFundingParamsStore((state) => state.state.planLevelFunding);
  const isPreFundCalc = useAccountFundingParamsStore((state) => state.state.isPreFundCalculated);
  const replenishmentFrequency = useAccountFundingParamsStore(
    (state) => state.state.replenishmentFrequency,
  );
  const isReplenished = useAccountFundingParamsStore((state) => state.state.isReplenished);
  const isForfeitDisabled = useMemo(() => !isReplenished
    || runOut === RunOutOption.NO_CLAIMS_DEADLINE, [isReplenished, runOut]);

  const previewMode = usePreviewStore((state) => state.previewMode);
  const appliedSourceState = useMemo(() => {
    if (previewMode) {
      return currentState;
    }
    return sourceState;
  }, [currentState, previewMode, sourceState]);

  const fundingReplenishmentFields: Field[] = useMemo(() => {
    if (isPlanLevelFunding && isReplenished && !isTemplateMode) {
      let replenishmentFields: Field[] = [];
      if (replenishmentFrequency === WEEKLY_KEY) {
        replenishmentFields = [{
          name: 'replenishmentDayNumber',
          type: FieldTypes.Dropdown,
          label: 'Day of the week',
          singleMode: true,
          options: [
            { key: '1', value: 'Monday' },
            { key: '2', value: 'Tuesday' },
            { key: '3', value: 'Wednesday' },
            { key: '4', value: 'Thursday' },
            { key: '5', value: 'Friday' },
          ],
          placeholder: 'Select frequency',
          showRequireIcon: true,
          fieldWrapperStyles: FIELD_WRAPPER_STYLES,
          validator: VALIDATORS.REQUIRED_STRING,
        }];
      }
      if (replenishmentFrequency === MONTHLY_KEY) {
        replenishmentFields = [{
          name: 'replenishmentDayNumber',
          label: 'Custom',
          type: FieldTypes.Number,
          lowerHelpText: CUSTOM_DAY_FIELD_HINT,
          showRequireIcon: true,
          fieldWrapperStyles: FIELD_WRAPPER_STYLES,
          placeholder: 'Enter custom day number',
          validator: VALIDATORS.REQUIRED_STRING.test({
            exclusive: false,
            test: (val) => {
              const customDayNumber = _.toNumber(val);
              return customDayNumber > 0 && customDayNumber <= 31;
            },
            message: CUSTOM_DAY_ERROR_TEXT,

          }).required(REQUIRED_TEXT),
        }];
      }
      return [
        {
          name: 'financeTitle',
          type: FieldTypes.Node,
          fieldWrapperStyles: FIELD_WRAPPER_STYLES,
          value: (
            <FinancialHeader
              title="Funding Replenishment"
              subTitle="Funds collected to replenish the plans account after claim payment have been made."
            />
          ),
        },
        {
          name: 'replenishmentBankAccount',
          type: FieldTypes.Dropdown,
          label: 'Bank account',
          singleMode: true,
          options: banksList,
          placeholder: 'Select bank account',
          showRequireIcon: true,
          validator: VALIDATORS.REQUIRED_STRING,
        }, {
          name: 'replenishmentMethod',
          type: FieldTypes.Radio,
          label: 'Method',
          options: FUNDING_REPLENISHMENT_METHOD_OPTIONS,
          defaultValue: FUNDING_REPLENISHMENT_METHOD_OPTIONS[0].value,
          showRequireIcon: true,
          disabled: true,
          validator: VALIDATORS.REQUIRED_STRING,
        }, {
          name: 'replenishmentFrequency',
          type: FieldTypes.Radio,
          label: 'Frequency',
          options: [
            { label: 'Daily', value: DAILY_KEY },
            { label: 'Weekly', value: WEEKLY_KEY },
            { label: 'Monthly', value: MONTHLY_KEY },
          ],
          showRequireIcon: true,
          // fieldWrapperStyles: replenishmentFrequency === DAILY_KEY ? FIELD_WRAPPER_STYLES : {},
          validator: VALIDATORS.REQUIRED_STRING,
        },

        ...replenishmentFields,
      ];
    }
    return [];
  }, [banksList, isPlanLevelFunding, isReplenished, isTemplateMode, replenishmentFrequency]);

  const preFundFields = useMemo(() => {
    if (isReplenished) {
      return [
        ...(isPreFundCalc ? [{
          name: 'preFundTitle',
          type: FieldTypes.Node,
          fieldWrapperStyles: FIELD_WRAPPER_STYLES,
          value: (
            <FinancialHeader
              title="Pre-Fund"
              subTitle={PRE_FUND_SUBTITLE}
            />
          ),
        },
        {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_PERCENT,
          type: FieldTypes.Number,
          disabled: !isEditMode,
          showRequireIcon: true,
          validator: isActivationMode
            ? percentValueValidate.required(REQUIRED_TEXT)
            : percentValueValidate,
          placeholder: 'Select Percent',
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
                Percent
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small">
                  {PRE_FUND_SUBFIELDS.PERCENT}
                </Inscription>
              </Box>
            </Box>
          ),
        },
        {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_MIN_VALUE,
          type: FieldTypes.Currency,
          disabled: !isEditMode,
          validator: isActivationMode
            ? currencyValueValidate.required(REQUIRED_TEXT)
            : currencyValueValidate,
          placeholder: 'Select Minimum Value',
          label: 'Minimum value',
          showRequireIcon: true,
        },
        {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_CREEP_PERCENT,
          type: FieldTypes.Number,
          disabled: !isEditMode,
          showRequireIcon: true,
          validator: isActivationMode
            ? percentValueValidate.required(REQUIRED_TEXT)
            : percentValueValidate,
          placeholder: 'Select Election Change Percentage',
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
                Election change percentage
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small">
                  {PRE_FUND_SUBFIELDS.ELECTION_CHANGE_PERCENTAGE}
                </Inscription>
              </Box>
            </Box>
          ),
        },
        {
          name: PLAN_SETUP_FIELD.REPLENISHMENT_CREEP_MIN,
          type: FieldTypes.Currency,
          disabled: !isEditMode,
          validator: isActivationMode
            ? currencyValueValidate.required(REQUIRED_TEXT)
            : currencyValueValidate,
          placeholder: 'Select Minimum Threshold',
          label: 'Minimum threshold',
          showRequireIcon: true,
          fieldWrapperStyles: ALTERNATIVE_FIELD_WRAPPER_STYLES,
        }] : []),
      ] as Field[];
    }
    return [];
  }, [isReplenished, isPreFundCalc, isEditMode, isActivationMode]);

  const payrollContributionFields: Field[] = useMemo(() => {
    if (isPlanLevelFunding && !isReplenished && !isTemplateMode) {
      return [
        {
          name: 'financeTitle',
          type: FieldTypes.Node,
          fieldWrapperStyles: {
            borderTop: `1px solid ${theme.colors.border1}`,
            paddingTop: theme.spacings.m,
          },
          value: (
            <FinancialHeader
              title="Payroll Contribution"
              subTitle="Elevate collects contributions made to employee-owned accounts, such as an HSA, prior to posting to the employee's account"
            />
          ),
        },
        {
          name: 'contributionBankAccount',
          type: FieldTypes.Dropdown,
          label: 'Bank account',
          singleMode: true,
          options: banksList,
          placeholder: 'Select bank account',
          showRequireIcon: true,
          validator: VALIDATORS.REQUIRED_STRING,
        }, {
          name: 'contributionMethod',
          type: FieldTypes.Radio,
          label: 'Method',
          options: [
            { label: 'Push funds', value: 'PULL_FUNDS' },
            { label: 'Pull funds', value: 'PUSH_FUNDS' },
          ],
          showRequireIcon: true,
          validator: VALIDATORS.REQUIRED_STRING,
        }, {
          name: 'contributionHoldPeriod',
          type: FieldTypes.Number,
          label: 'Hold period',
          regexp: REGEXPS.ONLY_NUMBERS,
          placeholder: 'Enter hold period',
          showRequireIcon: true,
          fieldWrapperStyles: {
            borderBottom: `1px solid ${theme.colors.border1}`,
            paddingBottom: theme.spacings.m,
          },
          validator: VALIDATORS.REQUIRED_POSITIVE_NUMBER_ONLY,
        }];
    }
    return [];
  }, [banksList, isPlanLevelFunding, isReplenished, isTemplateMode]);

  const forfeitFields = useMemo(() => ([
    {
      name: 'isForfeitAllowed',
      type: FieldTypes.Checkbox,
      disabled: isForfeitDisabled,
      label: (
        <Box flex={{ grow: 1 }}>
          <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
            Forfeit
          </Inscription>
          <Box>
            <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
              The forfeit option might be disabled when 'Run Out' is set to 'No Claims Deadline'.
            </Inscription>
          </Box>
        </Box>
      ),
      ...(isForfeitDisabled ? {
        name: 'isForfeitAllowed_disabled',
      } : {}),
    },
    ...(isForfeitDisabled || !isForfeitAllowed ? [] : [{
      name: 'forfeitureDays',
      type: FieldTypes.Number,
      placeholder: 'Enter # of days',
      label: (
        <Box flex={{ grow: 1 }}>
          <Inscription margin={{ bottom: 'spacing4' }} weight="bold" color="textBody">
            Forfeiture Days
          </Inscription>
          <Box>
            <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
              Forfeiture will occur a number of days after plan can no longer pay out claims (after any runout and rollover have completed).
            </Inscription>
          </Box>
        </Box>
      ),
      validator: yup.string().test({
        test: (val) => {
          if (runOut === RunOutOption.LAST_DAY_OF_PLAN_YEAR || runOut === RunOutOption.CUSTOM) {
            return _.toNumber(val) >= 1;
          }
          return true;
        },
        message: 'Number of the forfeiture days should be 1 or more.',
      }),
    }]),
  ]), [runOut, isForfeitAllowed, isForfeitDisabled]);

  const eoyFields = useEOYPlanReconciliationFields(
    isEditMode,
    undefined,
    currentState?.omnibus
  );

  const maintenanceFeeFields = useMemo(() => [
    {
      name: 'maintenanceFee',
      showRequireIcon: true,
      type: FieldTypes.Currency,
      label: 'Account maintenance fee',
      placeholder: 'Enter $ amount',
      validator: yup
        .string()
        .trim()
        .test({
          test: (val) => {
            if (val) {
              return _.toNumber(val) > 0;
            }
            return true;
          },
          message: MUST_BE_GREATER_TEXT(0),
        })
        .required(REQUIRED_TEXT),
    },
    {
      name: 'applyFeeTo',
      type: FieldTypes.Radio,
      showRequireIcon: true,
      label: 'Apply fee to',
      options: APPLY_FEE_TO_OPTIONS,
      validator:  yup.string().trim().nullable().required(REQUIRED_TEXT),
    },
    {
      name: 'cashBalanceThreshold',
      type: FieldTypes.Currency,
      label: 'Cash balance threshold',
      placeholder: 'Enter $ amount',
      validator: yup
        .string()
        .trim()
        .test({
          test: (val) => {
            if (val) {
              return _.toNumber(val) > 0;
            }
            return true;
          },
          message: MUST_BE_GREATER_TEXT(0),
        }),
    },
  ], []);

  const fields = useMemo(() => [
    {
      name: 'isReplenished',
      type: FieldTypes.Checkbox,
      label: 'Is replenished',
      validator: yup.string().test({
        test: (val) => val !== `${errorFields?.isReplenished?.value}`,
        message: errorFields?.isReplenished?.message,
      }),
    },
    {
      name: 'planLevelFunding',
      type: FieldTypes.Checkbox,
      label: 'Plan level funding',
      validator: VALIDATORS.STRING,
    },
    {
      name: 'isParticipantPaidMaintenanceFee',
      type: FieldTypes.Checkbox,
      label: 'Participant paid maintenance fee',
    },
    ...(isParticipantPaidMaintenanceFee ? maintenanceFeeFields : []),
    {
      name: PLAN_SETUP_FIELD.IS_PRE_FUND_CALCULATED,
      type: FieldTypes.Checkbox,
      label: 'Custom prefund calculation',
    },

    ...payrollContributionFields,

    ...fundingReplenishmentFields,
    ...preFundFields,

    {
      name: 'omnibus',
      type: FieldTypes.Dropdown,
      label: 'Omnibus',
      singleMode: true,
      options: [{ key: 'NOTIONAL', value: 'Notional' }, { key: 'HSA', value: 'HSA' }],
      placeholder: 'Select option',
      showRequireIcon: true,
      validator: isActivationMode
        ? VALIDATORS.REQUIRED_STRING
        : VALIDATORS.STRING,
    },
    {
      name: 'accountCreation',
      type: FieldTypes.Dropdown,
      label: 'Account creation',
      singleMode: true,
      options: [{ key: 'REUSE_WHEN_SAME_ACCOUNT', value: 'Re-use when same account type' }, { key: 'CREATE_NEW', value: 'Always create new' }],
      placeholder: 'Select option',
      showRequireIcon: true,
      validator: isActivationMode
        ? VALIDATORS.REQUIRED_STRING
        : VALIDATORS.STRING,
    },
    ...forfeitFields,
    ...eoyFields,
  ] as Field[], [
    fundingReplenishmentFields,
    isActivationMode,
    payrollContributionFields,
    preFundFields,
    errorFields,
    forfeitFields,
    isParticipantPaidMaintenanceFee,
    maintenanceFeeFields,
    eoyFields,
  ]);
  const filteredField = usePlanFieldsState(fields);
  const formattedFields = useFieldsWithDefaultValues(filteredField, appliedSourceState);

  const stateFields = useTemplateFieldState(formattedFields, isTemplateMode, isEditMode);
  return stateFields;
};

export default useAccountFundingParamsFields;
